import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import ContentCard from "@/components/feature/ContentCard";
import SectionHeading from "@/components/feature/SectionHeading";
import EmptyState from "@/components/feature/EmptyState";
import { movieList, searchEntries, sortEntries } from "@/utils/content";
import { usePageMeta } from "@/hooks/usePageMeta";
import { TYPE_LABEL, type ContentItem } from "@/types/content";

const tabs = ["全部", "电影", "电视剧", "资讯", "专题"];

function ResultRow({ item }: { item: ContentItem }) {
  const isVideo = item.type === "movie" || item.type === "tv";
  return (
    <Link
      to={`/detail/${item.id}`}
      className="group flex gap-4 rounded-lg bg-background-50 border border-background-200 p-4 hover:border-primary-300 transition-colors cursor-pointer"
    >
      <div
        className={`shrink-0 rounded-md overflow-hidden bg-background-200 ${
          isVideo ? "w-20 md:w-24 aspect-[2/3]" : "w-32 md:w-40 aspect-[16/10]"
        }`}
      >
        <img
          src={item.image}
          alt={item.title}
          title={`${item.title} 影视工厂`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="px-2 py-0.5 rounded text-xs bg-primary-50 text-primary-700">
            {TYPE_LABEL[item.type]}
          </span>
          <span className="px-2 py-0.5 rounded text-xs bg-secondary-100 text-secondary-900">
            {item.genres[0]}
          </span>
        </div>
        <h3 className="mt-2 text-base md:text-lg font-medium text-foreground-950 line-clamp-1 group-hover:text-primary-600 transition-colors">
          {item.title}
        </h3>
        <p className="mt-1.5 text-sm text-foreground-600 line-clamp-2">{item.summary}</p>
        <p className="mt-2 text-xs text-foreground-500 flex items-center gap-3 flex-wrap">
          <span className="flex items-center gap-1">
            <i className="ri-user-line" />
            {item.cast}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line" />
            {item.releaseDate}
          </span>
        </p>
      </div>
    </Link>
  );
}

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q") ?? "";
  const [input, setInput] = useState(keyword);
  const [tab, setTab] = useState("全部");

  usePageMeta(
    keyword ? `搜索「${keyword}」 - 影视工厂` : "全站搜索 - 影视工厂",
    "在影视工厂全站搜索电影、电视剧、影视资讯与专题内容，快速定位你感兴趣的影视资源。",
    "影视搜索,影视工厂,电影搜索,电视剧搜索,影视资讯"
  );

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const recommended = useMemo(() => sortEntries(movieList, "hot").slice(0, 4), []);

  const allResults = useMemo(() => searchEntries(keyword), [keyword]);
  const results = useMemo(
    () => (tab === "全部" ? allResults : allResults.filter((i) => TYPE_LABEL[i.type] === tab)),
    [allResults, tab]
  );

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const q = input.trim();
    if (q) {
      setParams({ q });
    } else {
      setParams({});
    }
    setTab("全部");
  };

  return (
    <div className="bg-background-50 min-h-[70vh]">
      <div className="bg-background-100 border-b border-background-200">
        <div className="mx-auto max-w-page px-4 md:px-6 py-8 md:py-12">
          <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 tracking-wide">
            全站搜索
          </h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600">
            输入关键词，快速检索电影、电视剧、资讯与专题内容
          </p>
          <form onSubmit={handleSubmit} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-2xl">
            <div className="flex-1 flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 h-12">
              <i className="ri-search-line text-foreground-400 text-lg" />
              <input
                type="text"
                name="q"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="搜索影视名称、主演、类型或关键词"
                className="flex-1 h-full bg-transparent outline-none text-sm text-foreground-950 placeholder:text-foreground-400"
              />
            </div>
            <button
              type="submit"
              className="h-12 px-8 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>
        </div>
      </div>

      <div className="mx-auto max-w-page px-4 md:px-6 py-8 md:py-12">
        {keyword && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              {tabs.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setTab(item)}
                  className={`px-4 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    item === tab
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-secondary-100 hover:text-secondary-900"
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>

            <p className="mt-6 text-sm text-foreground-600">
              共找到 <span className="font-medium text-primary-600">{results.length}</span> 条与「
              <span className="text-foreground-900">{keyword}</span>」相关的内容
            </p>
          </>
        )}

        {keyword ? (
          results.length > 0 ? (
            <div className="mt-6 flex flex-col gap-4">
              {results.map((item) => (
                <ResultRow key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <div className="mt-4">
              <EmptyState
                icon="ri-search-eye-line"
                title={`未找到与「${keyword}」相关的内容`}
                description="试试更换关键词，或者浏览下面为你推荐的热门影视内容。"
              />
            </div>
          )
        ) : (
          <div className="mt-4">
            <EmptyState
              icon="ri-search-line"
              title="输入关键词开始搜索"
              description="你可以搜索影视名称、主演、类型或资讯关键词，例如「科幻」「悬疑」「星际边界」。"
            />
          </div>
        )}

        <section className="mt-12 md:mt-16">
          <SectionHeading title="热门推荐" subtitle="这些内容同样值得一看" moreTo="/movie" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {recommended.map((item) => (
              <ContentCard key={item.id} item={item} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}